import { useMemo } from "react";
import { faGlobe } from "@fortawesome/free-solid-svg-icons";
import type { DevToolsPlugin } from "../../types";
import { FilterableList } from "../../FilterableList";
import type { FilterableSection } from "../../FilterableList";

interface Site {
  name: string;
  description: string;
  href: string;
}

interface SitesPluginProps {
  mainSites: Site[];
  ukSites: Site[];
  usSites: Site[];
  verticals: Record<string, string>;
}

const sortAlphabetically = (sites: Site[]) =>
  [...sites].sort((a, b) => a.description.localeCompare(b.description));

function renderChips(sites: Site[]) {
  return (
    <div className="dt-chip-grid">
      {sites.map((s) => (
        <a key={s.name} className="dt-chip" href={s.href}>{s.description}</a>
      ))}
    </div>
  );
}

function SitesPanel({ mainSites, ukSites, usSites, verticals }: SitesPluginProps) {
  const sortedUkSites = useMemo(() => sortAlphabetically(ukSites), [ukSites]);
  const sortedUsSites = useMemo(() => sortAlphabetically(usSites), [usSites]);
  const verticalSites: Site[] = useMemo(
    () => Object.entries(verticals).map(([name, href]) => ({ name, description: name, href })),
    [verticals],
  );

  const sections: FilterableSection<Site>[] = useMemo(
    () => [
      { key: "main", title: "Main Sites", items: mainSites, matchText: (s) => s.description, renderItems: renderChips },
      { key: "uk", title: "UK Partners", items: sortedUkSites, matchText: (s) => s.description, renderItems: renderChips },
      { key: "us", title: "US Partners", items: sortedUsSites, matchText: (s) => s.description, renderItems: renderChips },
      { key: "verticals", title: "Verticals", items: verticalSites, matchText: (s) => s.name, renderItems: renderChips },
    ],
    [mainSites, sortedUkSites, sortedUsSites, verticalSites],
  );

  return <FilterableList sections={sections} />;
}

export function sitesPlugin(props: SitesPluginProps): DevToolsPlugin {
  return {
    id: "sites",
    name: "Sites & Verticals",
    icon: faGlobe,
    render: () => <SitesPanel {...props} />,
  };
}
